import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelTemplate } from "@/components/common/Modals/CancelTemplate"
import FileInput from "@/components/upload/FileInput"
import type { InstructionCreateInput } from "@/graphql/codegen/graphql"
import { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { useImageUpload } from "@/hooks/useImageUpload"
import { NotificationType } from "@/schemas/notification"
import InstructionForm from "@/screens/ManageProducts/components/Instructions/components/form/InstructionForm"
import {
  CREATE_INSTRUCTION_CANCEL_CONFIRMATION_TEXT,
  CREATE_INSTRUCTION_CANCEL_CONFIRMATION_TITLE,
  CREATE_INSTRUCTION_DIALOG_TITLE,
  CREATE_INSTRUCTION_TOAST_MESSAGE,
} from "@/screens/ManageProducts/components/Instructions/constants"
import { useInstruction } from "@/screens/ManageProducts/components/Instructions/hooks/useInstruction"
import { InstructionFormSchema } from "@/screens/ManageProducts/components/Instructions/schema/InstructionsSchema"
import { InstructionDialogs } from "@/screens/ManageProducts/components/Instructions/types"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface CreateInstructionDialogProps {
  isOpen: InstructionDialogs | null
  onClose: () => void
  onSuccess: () => void
}

type CreateInstructionFormType = InstructionCreateInput & {
  image?: File
}

const CreateInstructionDialog: FC<CreateInstructionDialogProps> = ({ isOpen, onClose, onSuccess }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { createInstruction } = useInstruction()
  const methods = useForm<CreateInstructionFormType>({
    defaultValues: {
      applicableTo: [],
      isHazard: false,
      includeWith: [],
      name: "",
      description: "",
      transportationMode: [],
    },
    mode: "all",
    resolver: zodResolver(InstructionFormSchema),
  })
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const { uploadFile: uploadImageFile } = useImageUpload({
    referenceTableName: ReferenceTableName.Instruction,
  })

  const {
    formState: { isValid, isSubmitting, errors },
    handleSubmit,
  } = methods

  const handleSubmitData = async ({ image, ...fields }: CreateInstructionFormType) => {
    const { error, data } = await createInstruction({
      input: fields,
    })
    const hasError =
      error?.message ||
      (data?.instruction.create.__typename === "InstructionFailure" && Boolean(data?.instruction.create.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to create instruction",
      })
      return
    }

    if (image && data?.instruction.create.__typename === "InstructionCreateSuccess") {
      try {
        // Upload image
        const uploadSuccess = await uploadImageFile(
          image,
          FieldGroupEnum.Instruction,
          data?.instruction?.create.instruction.instructionId,
          "instruction_icon"
        )
        if (!uploadSuccess) {
          throw new Error(`Unable to upload image: ${image.name}. Please try again.`)
        }
      } catch (error) {
        notify({
          message: error instanceof Error ? error.message : "Unable to upload image. Please try again.",
          type: NotificationType.Error,
        })
      }
    }

    notify({
      type: NotificationType.Success,
      silent: false,
      content: CREATE_INSTRUCTION_TOAST_MESSAGE,
    })
    onSuccess()
  }

  const confirmCancel = () => {
    onClose()
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  return (
    <Dialog open={isOpen === InstructionDialogs.ADD_INSTRUCTION} onClose={handleCancel} fullWidth>
      {isCancelConfirmationOpen ? (
        <CancelTemplate
          onClose={setIsCancelConfirmationOpen}
          title={CREATE_INSTRUCTION_CANCEL_CONFIRMATION_TITLE}
          message={CREATE_INSTRUCTION_CANCEL_CONFIRMATION_TEXT}
          onConfirm={confirmCancel}
        />
      ) : (
        <>
          <DialogTitle className='pl-10 font-normal text-primary'>{CREATE_INSTRUCTION_DIALOG_TITLE}</DialogTitle>
          <IconButton aria-label='close' onClick={handleCancel} className='absolute right-4 top-4 text-primary'>
            <CloseOutlined />
          </IconButton>
          <Divider />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <InstructionForm />
              <Box className='mt-4'>
                <FileInput title='Upload Instruction Icon' name='image' />
              </Box>
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              disabled={!isValid || isSubmitting || Boolean(Object.keys(errors).length)}
              onClick={handleSubmit(handleSubmitData)}
            >
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default CreateInstructionDialog
